import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import * as queryString from "query-string";
import { SUPPORTED_LOCALES } from '../../constants/locales';

export const getStrippedPath = (pathname) => {
  // Strip lang and widget from pathname
  const localeRegex = new RegExp(`^/(${SUPPORTED_LOCALES.join('|')})`, 'i');
  let strippedPath = pathname.replace(localeRegex, "");
  strippedPath = strippedPath.replace("/widget", "") + "/"; // ensure trailing slash
  strippedPath = strippedPath.replace(/\/+/g, '/'); // remove double slashes

  return strippedPath;
};

export const getCanonicalParams = (search) => {
  const canonicalAllowedQueryParams = ["fromLocationCode", "toLocationCode"];
  const parsed = queryString.parse(search);
  const pickedParams = {};
  canonicalAllowedQueryParams.forEach(key => {
    if (parsed[key]) pickedParams[key] = parsed[key];
  });
  return Object.keys(pickedParams).length > 0 ? '?' + queryString.stringify(pickedParams) : '';
};

export const getCanonicalUrl = (strippedPath, canonicalParams, currentLang) => {
  // Always use the current language for canonical URLs.
  const origin = window.location.origin;

  let location;
  if (canonicalParams && canonicalParams !== '') {
    // If there are canonical params, canonical should point to the language root
    // for the current language.
    location = `${origin}/${currentLang}/`;
  } else {
    // Special hardcoded case: when browsing the Hebrew homepage, canonical should point
    // to the main domain without the subdomain (https://paapmpaapm.com/)
    if (currentLang === 'he' && strippedPath === '/') {
      return 'https://paapmpaapm.com/';
    }

    location = `${origin}/${currentLang}${strippedPath}`;
  }

  return `${location}${canonicalParams}`;
};

const MyHelmet = ({ lng, redirect = false }) => {
  const history = useHistory();
  const { t } = useTranslation();

  // trying to reduce duplicate content by setting canonical to default language
  // based on old google guide: https://developers.google.com/search/blog/2010/09/unifying-content-under-multilingual
  // to monitor if this helps with reducting 2.6K duplicate pages indexed by google: https://search.google.com/search-console/index/drilldown?resource_id=sc-domain%3Apaapmpaapm.com&item_key=CAMYECAC
  const strippedPath = getStrippedPath(window.location.pathname);
  const canonicalParams = getCanonicalParams(window.location.search);
  const canonical = getCanonicalUrl(strippedPath, canonicalParams, lng);

  // For redirect check, use current domain
  const currentLocalizedPath = `${window.location.origin}/${lng}${strippedPath}`.replace(/\/+/g, '/');

  const alternates = [];

  // for now only set alternatives for the hompage (without canonical params) so we won't put links to unwanted location pages
  // this will cause only he location pages to be visible to google and stop linking to the non-he versions, which should help with crawl budget
  if (strippedPath === '/' && !canonicalParams) {
    // Use all supported locales for alternates
    SUPPORTED_LOCALES.forEach(language => {
      alternates.push(
        <link rel="alternate" href={`${window.location.origin}/${language}${strippedPath}${canonicalParams}`} hrefLang={language} key={language} />
      );
    });
  }

  const params = {
    "title": t('headTitle'),
    "og:title": t('headTitle'),
    "description": t('headDescription'),
    "og:description": t('headDescription')
  };

  useEffect(() => {
    if (redirect && currentLocalizedPath !== `${window.location.origin}${window.location.pathname}`) {
      // Strip 'lng' from querystring to avoid redundancy after detection
      const cleanedSearch = queryString.exclude(window.location.search, ['lng']);
      history.replace(`/${lng}${strippedPath}${cleanedSearch}`)
    }
  }, [currentLocalizedPath, lng, strippedPath, redirect]);

  return (
    <Helmet>
      <title>{params["title"]}</title>
      <meta property="og:title" content={params["og:title"]} />
      <meta property="og:description" content={params["og:description"]} />
      <meta name="description" content={params["description"]} />
      <link rel="canonical" href={canonical} />
      <html lang={lng} />
      {lng !== 'he' && canonicalParams && <meta name="robots" content="noindex" />}
      {alternates}
    </Helmet>
  );
};

export default MyHelmet;